// # General Settings Tab Component
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../auth/state';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../auth/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { countryCodes } from '../../../utils/phone_code';
import { nationalityData } from '../../../utils/nationality_data';
import PropTypes from 'prop-types';
import './general_settings.css';

function GeneralSettings({ isDarkMode }) {
  const { user, userProfile, updateUserProfile, signOut } = useAuthStore();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    phoneNumber: '', // # Store phone number without country code
    selectedCountry: { code: '+20', name_en: 'Egypt', flag: '🇪🇬' }, // Default to Egypt
    university: '',
    phase: '',
    gpa: '',
    nationality: 'EGYPT', // Default to Egypt
    gender: '',
    profile: {
      biography: '', // # Store biography within profile map
      socialLinks: {  // # Add social links
        facebook: '',
        twitter: '',
        tiktok: '',
        instagram: ''
      }
    }
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  // # Load user data into form
  useEffect(() => {
    if (userProfile) {
      // # Get the selectedCountry directly from userProfile or use default
      const selectedCountry = userProfile.selectedCountry || { 
        code: '+20', 
        name_en: 'Egypt', 
        flag: '🇪🇬' 
      };

      // # Extract phone number without country code
      let phoneNumber = userProfile.phoneNumber || '';
      if (phoneNumber.startsWith('+')) {
        // # Remove the country code from phone number
        phoneNumber = phoneNumber.substring(selectedCountry.code.length);
      }

      setFormData({
        firstName: userProfile.firstName || '',
        middleName: userProfile.middleName || '',
        lastName: userProfile.lastName || '',
        phoneNumber: phoneNumber, // # Store without country code
        selectedCountry: selectedCountry,
        university: userProfile.university || '',
        phase: userProfile.phase || '',
        gpa: userProfile.gpa || '',
        nationality: userProfile.nationality || 'EGYPT',
        gender: userProfile.gender || '',
        profile: {
          ...userProfile.profile,
          biography: userProfile.profile?.biography || '',
          socialLinks: {
            facebook: userProfile.profile?.socialLinks?.facebook || '',
            twitter: userProfile.profile?.socialLinks?.twitter || '',
            tiktok: userProfile.profile?.socialLinks?.tiktok || '',
            instagram: userProfile.profile?.socialLinks?.instagram || ''
          }
        }
      });
    }
  }, [userProfile]);

  // # Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'biography') {
      // # Special handling for biography within profile map
      setFormData(prev => ({
        ...prev,
        profile: {
          ...prev.profile,
          biography: value
        }
      }));
    } else if (name === 'facebook' || name === 'twitter' || name === 'tiktok' || name === 'instagram') {
      // # Handle social links updates
      setFormData(prev => ({
        ...prev,
        profile: {
          ...prev.profile,
          socialLinks: {
            ...prev.profile.socialLinks,
            [name]: value
          }
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // # Handle country code change
  const handleCountryChange = (e) => {
    const country = countryCodes.find(c => c.code === e.target.value);
    setFormData(prev => ({
      ...prev,
      selectedCountry: country
    }));
  };

  // # Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      console.log('Updating user profile for:', user.uid);
      const startTime = performance.now();

      // # Ensure country code starts with '+' 
      const countryCode = formData.selectedCountry.code.startsWith('+') ? 
        formData.selectedCountry.code : 
        `+${formData.selectedCountry.code}`;
      
      // # Remove any leading zeros from phone number
      const cleanPhoneNumber = formData.phoneNumber.replace(/^0+/, '');

      // # Prepare update data
      const updateData = {
        firstName: formData.firstName,
        middleName: formData.middleName,
        lastName: formData.lastName,
        phoneNumber: cleanPhoneNumber, // # Store only the number part without country code
        nationality: formData.nationality,
        university: formData.university,
        phase: formData.phase,
        gpa: formData.gpa,
        gender: formData.gender,
        selectedCountry: {
          code: countryCode, // # Store country code separately
          flag: formData.selectedCountry.flag,
          name_en: formData.selectedCountry.name_en
        },
        profile: {
          ...formData.profile
        },
        'metadata.updatedAt': new Date().toISOString()
      };

      // # Update Firestore document
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, updateData);

      const endTime = performance.now();
      console.log('User profile update completed:', {
        userId: user.uid,
        readTime: `${(endTime - startTime).toFixed(2)}ms`,
        timestamp: new Date().toISOString()
      });

      // # Update local state and show success message
      setIsEditing(false);
      setShowSuccess(true);
      
      // Hide success message after animation
      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // # Handle delete modal
  const openDeleteModal = () => {
    setShowDeleteModal(true);
    setDeleteConfirmation('');
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteConfirmation('');
  };

  // # Handle account deletion
  const handleDeleteAccount = async () => {
    if (deleteConfirmation.toLowerCase() !== 'delete') {
      return;
    }

    try {
      setIsDeleting(true);
      const functions = getFunctions(undefined, 'europe-west1');
      const userSelfDelete = httpsCallable(functions, 'userSelfDelete');
      
      await userSelfDelete();
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error deleting account:', error);
      alert('Failed to delete account. Please try again or contact support.');
    } finally {
      setIsDeleting(false);
      closeDeleteModal();
    }
  };

  return (
    <div className={`gen_st_section ${isDarkMode ? 'stng_mn_dark-mode' : ''}`}>
      {showSuccess && (
        <div className="gen_st_success-message">
          <i className="fas fa-check-circle"></i>
          Profile updated successfully!
        </div>
      )}
      
      <div className="gen_st_header">
        <h2>Personal Information</h2>
        {!isEditing && (
          <button 
            className="gen_st_edit-button"
            onClick={() => setIsEditing(true)}
          >
            <i className="fas fa-edit"></i> Edit
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="gen_st_form">
        <div className="gen_st_form-grid">
          {/* # Name fields */}
          <div className="gen_st_form-group">
            <label htmlFor="firstName">First Name</label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              disabled={!isEditing}
              required
            />
          </div>

          <div className="gen_st_form-group">
            <label htmlFor="middleName">Middle Name</label>
            <input
              type="text"
              id="middleName"
              name="middleName"
              value={formData.middleName}
              onChange={handleChange}
              disabled={!isEditing}
            />
          </div>

          <div className="gen_st_form-group">
            <label htmlFor="lastName">Last Name</label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              disabled={!isEditing}
              required
            />
          </div>

          {/* # Contact and Personal Info */}
          <div className="gen_st_form-group">
            <label htmlFor="phoneNumber">Phone Number</label>
            <div className="gen_st_phone-input">
              <select
                className="gen_st_country-code"
                value={formData.selectedCountry.code}
                onChange={handleCountryChange}
                disabled={!isEditing}
              >
                {countryCodes.map((country) => (
                  <option key={`${country.code}_${country.country}`} value={country.code}>
                    {country.flag} {country.code}
                  </option>
                ))}
              </select>
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                disabled={!isEditing}
                className="gen_st_phone-number"
              />
            </div>
          </div>

          <div className="gen_st_form-group">
            <label htmlFor="nationality">Nationality</label>
            <select
              id="nationality"
              name="nationality"
              value={formData.nationality}
              onChange={handleChange}
              disabled={!isEditing}
            >
              <option value="">Select Nationality</option>
              {Object.entries(nationalityData).map(([key, data]) => (
                <option key={key} value={key}>
                  {data.flag} {data.name}
                </option>
              ))}
            </select>
          </div>

          <div className="gen_st_form-group">
            <label htmlFor="gender">Gender</label>
            <select
              id="gender"
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              disabled={!isEditing}
            >
              <option value="">Select Gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* # Academic Information */}
          <div className="gen_st_form-group">
            <label htmlFor="university">University</label>
            <input
              type="text"
              id="university"
              name="university"
              value={formData.university}
              onChange={handleChange}
              disabled={!isEditing}
            />
          </div>

          <div className="gen_st_form-group">
            <label htmlFor="phase">Current Phase</label>
            <select
              id="phase"
              name="phase"
              value={formData.phase}
              onChange={handleChange}
              disabled={!isEditing}
            >
              <option value="">Select Your Current Phase</option>
              <option value="basic-science">Academic Phase (Basic Science)</option>
              <option value="clinical-medicine">Clinical Phase (Clinical Medicine)</option>
              <option value="intern">Intern</option>
              <option value="new-graduate">New Graduate (&lt; 3 years)</option>
              <option value="old-graduate">Old Graduate (&gt; 3 years)</option>
            </select>
          </div>

          <div className="gen_st_form-group">
            <label htmlFor="gpa">GPA</label>
            <input
              type="number"
              id="gpa"
              name="gpa"
              value={formData.gpa}
              onChange={handleChange}
              disabled={!isEditing}
              step="0.01"
              min="0"
              max="4"
            />
          </div>

          {/* # Biography textarea with updated value path */}
          <div className="gen_st_biography-group">
            <label htmlFor="biography">Biography</label>
            <textarea
              id="biography"
              name="biography"
              value={formData.profile.biography}
              onChange={handleChange}
              disabled={!isEditing}
              maxLength={500}
              placeholder="Tell us about yourself..."
            />
            <div className="gen_st_char-count">
              {formData.profile.biography.length}/500 characters
            </div>
          </div>

          {/* # Add Social Links Section */}
          <div className="gen_st_social-links-group">
            <label>Social Media Links</label>
            <div className="gen_st_social-links-grid">
              <div className="gen_st_social-input">
                <i className="fab fa-facebook" style={{ color: '#1877f2' }}></i>
                <input
                  type="url"
                  name="facebook"
                  value={formData.profile.socialLinks.facebook}
                  onChange={handleChange}
                  disabled={!isEditing}
                  placeholder="Facebook URL"
                />
              </div>
              <div className="gen_st_social-input">
                <span className="gen_st_x-logo">𝕏</span>
                <input
                  type="url"
                  name="twitter"
                  value={formData.profile.socialLinks.twitter}
                  onChange={handleChange}
                  disabled={!isEditing}
                  placeholder="X (Twitter) URL"
                />
              </div>
              <div className="gen_st_social-input">
                <i className="fab fa-tiktok" style={{ color: '#000000' }}></i>
                <input
                  type="url"
                  name="tiktok"
                  value={formData.profile.socialLinks.tiktok}
                  onChange={handleChange}
                  disabled={!isEditing}
                  placeholder="TikTok URL"
                />
              </div>
              <div className="gen_st_social-input">
                <i className="fab fa-instagram" style={{ color: '#e4405f' }}></i>
                <input
                  type="url"
                  name="instagram"
                  value={formData.profile.socialLinks.instagram}
                  onChange={handleChange}
                  disabled={!isEditing}
                  placeholder="Instagram URL"
                />
              </div>
            </div>
          </div>
        </div>

        {isEditing && (
          <div className="gen_st_form-actions">
            <button 
              type="button" 
              className="gen_st_cancel-button"
              onClick={() => setIsEditing(false)}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="gen_st_save-button"
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </form>

      {/* # Account Actions Section */}
      <div className="gen_st_actions-section">
        <h3 className="gen_st_actions-header">Account Actions</h3>
        <div className="gen_st_action-item">
          <div className="gen_st_action-info">
            <div className="gen_st_action-title">Delete Account</div>
            <div className="gen_st_action-description">
              Permanently delete your account and all associated data. This action cannot be undone.
            </div>
          </div>
          <button 
            className="gen_st_delete-button"
            onClick={openDeleteModal}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete Account'}
          </button>
        </div>
      </div>

      {/* # Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="gen_st_delete-modal">
          <div className="gen_st_delete-modal-content">
            <div className="gen_st_delete-modal-header">
              <i className="fas fa-exclamation-triangle"></i>
              Delete Account Confirmation
            </div>
            <div className="gen_st_delete-modal-body">
              <div className="gen_st_delete-modal-message">
                <p>Are you absolutely sure you want to delete your account? This action cannot be undone and will permanently delete:</p>
                <ul>
                  <li>Your personal information</li>
                  <li>Your course progress</li>
                  <li>Your saved notes and materials</li>
                  <li>All associated data</li>
                </ul>
              </div>
              <input
                type="text"
                className="gen_st_delete-confirm-input"
                placeholder="Type 'delete' to confirm"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
              />
            </div>
            <div className="gen_st_delete-modal-actions">
              <button 
                className="gen_st_delete-cancel-button"
                onClick={closeDeleteModal}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button 
                className="gen_st_delete-confirm-button"
                onClick={handleDeleteAccount}
                disabled={deleteConfirmation.toLowerCase() !== 'delete' || isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

GeneralSettings.propTypes = {
  isDarkMode: PropTypes.bool
};

GeneralSettings.defaultProps = {
  isDarkMode: false
};

export default GeneralSettings;